import {
  CheckIcon,
  XMarkIcon,
  PencilIcon,
  Cog6ToothIcon,
  ArrowRightStartOnRectangleIcon,
} from '@heroicons/react/24/solid';
import { ReactFlowProvider } from '@xyflow/react';
import { format } from 'date-fns';
import { useState, useEffect } from 'react';
import { Tooltip } from 'react-tooltip';
import { Button } from 'src/components/button';
import Input from 'src/components/input';
import { appConfig, APP_VERSION, defaultProjectData } from 'src/config';
import { useGlobalContext } from 'src/context/global/use-global-context';
import { useUndoRedo, UndoRedoProvider } from 'src/context/undo-redo';
import { WizardProvider } from 'src/context/wizard/wizard-provider';

import Layout from '../layout';
import DialogDownloadFile from './dialog/download-file';
import DialogExportSettings from './dialog/export-settings';
import DialogGlobalSettings from './dialog/global-settings';
import DialogPackage from './dialog/package';
import DialogProjectData from './dialog/project-data';
import DialogReleaseNotes from './dialog/release-notes';
import DialogSetting from './dialog/setting';
import DialogUserSetting from './dialog/user-setting';
import DialogVlan from './dialog/vlan';
import Flow from './flow';

const NetworkConfiguration = () => {
  const { showDialogs, user, logout, lastChanged, projectData } =
    useGlobalContext();

  const [userSetting, setUserSetting] = useState(false);
  const [showReleaseNotes, setShowReleaseNotes] = useState(false);

  // useEffect(() => {
  //   if (window.location.pathname !== '/') {
  //     window.location.replace('/');
  //   }
  // }, []);

  return (
    <Layout>
      <ReactFlowProvider>
        <WizardProvider>
          <UndoRedoProvider
            options={{
              maxHistorySize: 100,
              enableShortcuts: true,
              enableLocalStorage: appConfig.enableHistoryLocalStorage,
            }}
          >
            <div
              data-testid="network-configuration-container"
              className="relative flex h-full w-full flex-row"
            >
              <div
                data-testid="network-configuration-header-container"
                className="absolute left-0 right-0 top-0 z-50 flex w-auto items-center justify-between bg-white px-4 py-4 shadow-sm"
              >
                <img
                  src="/logo-studio.png"
                  alt="logo studio"
                  className="h-12"
                />
                <div className="mx-auto flex flex-col items-center justify-center p-2">
                  <ProjectName />
                  {lastChanged && (
                    <span className="pr-8 text-sm text-gray-500">
                      Last changed:{' '}
                      {format(new Date(lastChanged), 'yyyy-MM-dd HH:mm:ss')}
                    </span>
                  )}
                </div>
                <img src="/logo.png" alt="logo" className="h-12" />
              </div>

              <div
                data-testid="network-configuration-footer-container"
                className="absolute bottom-4 right-4 z-50 flex w-auto"
              >
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    data-testid="network-configuration-logout-button"
                    onClick={logout}
                    className="flex items-center gap-2 rounded-md bg-black/30 px-2 py-1 text-sm text-white"
                  >
                    <span>{user?.name}</span>
                    <ArrowRightStartOnRectangleIcon className="h-4 w-4" />
                  </button>
                  <button
                    type="button"
                    data-testid="network-configuration-user-setting-button"
                    title="Manage User"
                    onClick={() => setUserSetting(true)}
                    className="flex items-center gap-2 rounded-md bg-black/30 px-2 py-1 text-sm text-white"
                  >
                    <Cog6ToothIcon className="h-5 w-5" />
                  </button>
                  <button
                    type="button"
                    data-testid="network-configuration-version-button"
                    title="Version & Release Notes"
                    onClick={() => setShowReleaseNotes(true)}
                    className="rounded-md bg-black/30 px-2 py-1 text-sm text-white hover:bg-black/50"
                  >
                    v{APP_VERSION}
                  </button>
                </div>
              </div>
              {/* <FlowSidebar /> */}
              <Flow />
            </div>
            {showDialogs.vlan && <DialogVlan />}
            {showDialogs.package && <DialogPackage />}
            {showDialogs.setting && <DialogSetting />}
            {showDialogs.downloadFile && <DialogDownloadFile />}
            {showDialogs.globalSettings && <DialogGlobalSettings />}
            {showDialogs.projectData && <DialogProjectData />}
            {userSetting && (
              <DialogUserSetting onCancel={() => setUserSetting(false)} />
            )}
            {showReleaseNotes && (
              <DialogReleaseNotes onClose={() => setShowReleaseNotes(false)} />
            )}
            {showDialogs.exportSettings && (
              <DialogExportSettings
                defaultSettings={
                  projectData?.exportSettings ||
                  defaultProjectData.exportSettings
                }
              />
            )}
            <Tooltip
              data-testid="app-tooltip"
              id="app-tooltip"
              className="tooltip"
              place="top"
            />
            <Tooltip
              data-testid="vlan-tooltip"
              id="vlan-tooltip"
              className="tooltip"
              place="top"
            />
          </UndoRedoProvider>
        </WizardProvider>
      </ReactFlowProvider>
    </Layout>
  );
};

const ProjectName = () => {
  const { projectData, setProjectData } = useGlobalContext();
  const [isEditing, setIsEditing] = useState(false);
  const [name, setName] = useState(projectData?.projectName);
  const { takeSnapshot } = useUndoRedo();

  const handleSave = () => {
    // take snapshot
    takeSnapshot();
    setProjectData({
      ...projectData,
      projectName: name,
    });
    setIsEditing(false);
  };

  useEffect(() => {
    setName(projectData?.projectName);
  }, [projectData?.projectName]);

  return (
    <div
      data-testid="project-name-container"
      className="group flex items-center gap-2"
    >
      {!isEditing ? (
        <div
          data-testid="project-name-span"
          className="flex items-center gap-2"
        >
          <span
            data-testid="project-name-span-text"
            className="text-lg font-bold text-black"
          >
            {projectData?.projectName}
          </span>
          <Button
            type="button"
            size="sm"
            data-testid="project-name-edit-button"
            className="invisible group-hover:visible"
            onClick={() => setIsEditing(true)}
          >
            <PencilIcon className="h-4 w-4" />
          </Button>
        </div>
      ) : (
        <>
          <Input
            data-testid="project-name-input"
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="rounded-md border border-gray-300 px-2 py-1 text-sm"
          />
          <Button
            type="button"
            size="sm"
            color="green"
            onClick={handleSave}
            data-testid="project-name-save-button"
          >
            <CheckIcon className="h-5 w-5" />
          </Button>
          <Button
            type="button"
            size="sm"
            data-testid="project-name-cancel-button"
            color="red"
            onClick={() => setIsEditing(false)}
          >
            <XMarkIcon className="h-5 w-5" />
          </Button>
        </>
      )}
    </div>
  );
};

export default NetworkConfiguration;
